import { useState } from 'react'
import { useToast } from '@/hooks/use-toast'
import { api } from '@/utils/api'
import { watchlistService } from '@/services'
import { refreshWatchlist } from '@/utils/watchlist-refresh'

interface UseCompetitorActionsParams {
  watchlistId: string
  watchlistName: string
  refetch: () => Promise<any> | any
  onNewWatchlistId?: (id: string) => void
}

export function useCompetitorWatchlistActions({
  watchlistId,
  watchlistName,
  refetch,
  onNewWatchlistId,
}: UseCompetitorActionsParams) {
  const { toast } = useToast()
  const [refreshingId, setRefreshingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean
    watchlistId: string
    watchlistName: string
  }>({
    open: false,
    watchlistId: '',
    watchlistName: ''
  })

  const openDeleteDialog = () => {
    setDeleteDialog({ open: true, watchlistId, watchlistName })
  }

  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, watchlistId: '', watchlistName: '' })
  }

  const handleDeleteWatchlist = async () => {
    setDeletingId(watchlistId)
    try {
      const result = await watchlistService.deleteWatchlist(watchlistId)
      if (result.success) {
        toast({
          title: 'Competitor watchlist deleted',
          description: `"${watchlistName}" has been successfully deleted.`,
          variant: 'default',
        })
        await refetch()
        closeDeleteDialog()
      } else {
        toast({ title: 'Error', description: 'Failed to delete competitor watchlist. Please try again.', variant: 'destructive' })
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete competitor watchlist. Please try again.', variant: 'destructive' })
    } finally {
      setDeletingId(null)
    }
  }

  const handleRefreshWatchlist = async () => {
    setRefreshingId(watchlistId)
    try {
      const result = await refreshWatchlist(watchlistId)
      if (result.success) {
        if (result.newWatchlistId) {
          try {
            await api.post('/v0_1/user/profile/competitor-list', { CompetitorList: result.newWatchlistId })
          } catch {
            // non-blocking
          }
        }
        toast({ title: 'Success', description: result.message })
        await refetch()
        if (result.newWatchlistId && onNewWatchlistId) onNewWatchlistId(result.newWatchlistId)
      } else {
        toast({ title: 'Refresh Failed', description: result.message, variant: 'destructive' })
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to refresh competitor watchlist. Please try again.', variant: 'destructive' })
    } finally {
      setRefreshingId(null)
    }
  }

  return {
    refreshingId,
    deletingId,
    deleteDialog,
    openDeleteDialog,
    closeDeleteDialog,
    handleDeleteWatchlist,
    handleRefreshWatchlist,
  }
}


